import type { MedplumRequestOptions } from '@medplum/core'
import type {
  ExtendedResource,
  ExtendedResourceConstructor,
} from '@seen/core/ExtendedResource'
import {
  getSearchKey,
  normalizeQuery,
  type QueryTypes,
} from '@seen/core/search'
import {
  skipToken,
  type UseQueryOptions,
  type UseQueryResult,
} from '@tanstack/react-query'
import { NO_QUERY } from '#src/constants/no-query.ts'
import { cacheKeys } from '#src/hooks/cacheKeys.ts'
import { useErrorToast } from '#src/hooks/useErrorToast.ts'
import { useMedplum } from '#src/hooks/useMedplum.ts'
import { useQuery } from '#src/hooks/useQuery.ts'

export interface UseSearchOneOptions<T> {
  search?: MedplumRequestOptions
  query?: Omit<
    UseQueryOptions<
      T | null,
      Error,
      T | null,
      ReturnType<(typeof cacheKeys)['searchOneExtended']>
    >,
    'initialData' | 'queryKey' | 'queryFn' | 'keepPreviousData'
  >
}

export function useSearchOne<T extends ExtendedResource>(
  resourceClass: ExtendedResourceConstructor<T>,
  query: QueryTypes | undefined,
  options: UseSearchOneOptions<T> = {}
): UseQueryResult<T | null> {
  const medplum = useMedplum()
  const resourceType = resourceClass.RESOURCE_TYPE
  const parameters = normalizeQuery(query ? { ...query, _count: 1 } : undefined)

  const searchKey = query
    ? getSearchKey(medplum, resourceType, parameters)
    : NO_QUERY

  const queryResult = useQuery({
    ...options?.query,
    // oxlint-disable-next-line @tanstack/query/exhaustive-deps
    queryKey: cacheKeys.searchOneExtended(resourceType, searchKey),
    queryFn:
      searchKey === NO_QUERY
        ? skipToken
        : async () =>
            medplum.searchOneResourceClass(
              resourceClass,
              parameters,
              options.search
            ),
    meta: {
      ...options?.query?.meta,
      queryParams: parameters,
      resourceClass,
    },
  })

  useErrorToast(
    queryResult.error &&
      `Error accessing ${searchKey}: ${queryResult.error.message ?? 'Unknown error'}`
  )

  return queryResult
}
